import { isEmpty, path } from 'ramda'

import {
  DEVICE_AWS_MQTTS_BLOCKED,
  DEVICE_AWS_OFFLINE,
  DEVICE_AWS_ONLINE,
  DEVICE_AWS_UNKNOWN,
} from '@common/components/DeviceAwsStatusIcon'
import { generateColors, isAbortError } from '@common/utils'

import {
  LAYER_AP,
  LAYER_AWS_STATUS,
  LAYER_CONN_GUIDANCE_MODE,
  LAYER_DATA,
  LAYER_ERROR,
  LAYER_FW,
  LAYER_MIC,
  LAYER_NOISE,
  LAYER_OPERATIONAL_STATE,
  LAYER_PROVISION,
  LAYER_RESET_REASON,
  LAYER_RSSI,
  LAYER_SNOOZE,
  LAYER_SSID,
  LAYER_SSID_MIGRATION,
  LAYER_STATUS,
} from './components/LayerSelector'

const CONNECTIVITY_MAP_DATA_LOADING = 'CONNECTIVITY_MAP_DATA_LOADING'
const CONNECTIVITY_MAP_DATA_LOADED = 'CONNECTIVITY_MAP_DATA_LOADED'
const CONNECTIVITY_MAP_DATA_FAILED = 'CONNECTIVITY_MAP_DATA_FAILED'

const CONNECTIVITY_MAP_RESET_STATE = 'CONNECTIVITY_MAP_RESET_STATE'

const defaultState = {}

const entityName = 'connectivityMap'

const isNumberRegexp = /^[1-9]\d*$/

const isValidFloor = (floor) => isNumberRegexp.test(floor) && Number(floor) < 150

const parseUnitName = (name, floor) => {
  // if floor is not valid it should be added to the `invalid` floor column
  const originalFloor = floor && isValidFloor(floor) ? floor : null
  const originalName = name

  if (isNumberRegexp.test(name)) {
    const len = name.length
    let predictedFloor = null
    let predictedName = null

    if (len === 3) {
      // eslint-disable-next-line prefer-destructuring
      predictedFloor = name[0]
      predictedName = name.slice(1)
    } else if (len === 4) {
      predictedFloor = name.slice(0, 2)
      predictedName = name.slice(2)
    } else if (len === 5) {
      predictedFloor = name.slice(0, 2)
      predictedName = name.slice(2)
    } else {
      predictedFloor = name.slice(0, len - 2)
      predictedName = name.slice(-2)
    }

    return {
      predictedFloor: floor ? null : String(Number(predictedFloor)),
      predictedName: String(Number(predictedName)),
      originalName: name,
      originalFloor,
    }
  }

  return { originalFloor, originalName }
}

export default {
  name: entityName,
  reducer: (state, action) => {
    if (action.type === CONNECTIVITY_MAP_RESET_STATE) {
      return defaultState
    }
    if (action.type === CONNECTIVITY_MAP_DATA_LOADING) {
      return { ...state, data: { ...action.meta, payload: action.payload } }
    }
    if (action.type === CONNECTIVITY_MAP_DATA_LOADED) {
      return { ...state, data: { ...action.meta, data: action.payload } }
    }
    if (action.type === CONNECTIVITY_MAP_DATA_FAILED) {
      return { ...state, data: { ...action.meta, error: action.payload } }
    }
    return state || defaultState
  },
  selectConnectivityMapDataIsLoading: ({ connectivityMap }) => {
    const status = path(['data', 'status'], connectivityMap)
    return status === 'loading'
  },
  selectConnectivityMapData: ({ connectivityMap }) =>
    path(['data', 'data'], connectivityMap),
  selectConnectivityMapDataError: ({ connectivityMap }) =>
    path(['data', 'error'], connectivityMap),
  doResetConnectivityMapState:
    () =>
    ({ dispatch }) =>
      dispatch({ type: CONNECTIVITY_MAP_RESET_STATE }),
  doFetchConnectivityMapData:
    (payload) =>
    async ({ dispatch, apiFetch, store }) => {
      try {
        dispatch({
          type: CONNECTIVITY_MAP_DATA_LOADING,
          payload,
          meta: { status: 'loading' },
        })

        const { property } = payload

        const apiResponse = await apiFetch(
          `/properties/${property.id}/connectivity_map/`,
          null,
          { cancelationPrefix: entityName },
        )

        const unitStatuses = store.selectSystemUnitStatus()
        const operationalStates = store
          .selectSystemOperationalStates()
          .map((item) => item.value)
        const connectivityGuidanceModes = store
          .selectSystemConnectivityGuidanceModes()
          .map((item) => item.value)
        const resetReasons = store
          .selectSystemDeviceResetReasons()
          .map((item) => item.id)

        const floors = new Set()
        const buckets = new Set()

        const uniqFW = new Set()
        const uniqAP = new Set()
        const uniqSSID = new Set()

        const legendCounts = Object.keys(LAYER_DATA).reduce(
          (acc, layer) => ({
            ...acc,
            [layer]: {},
          }),
          {},
        )

        const mappedUnits = {}
        const tableRows = []

        const getHumanizedRssi = (rssi) => {
          if (rssi === null || rssi === undefined) return 'UNKNOWN'
          if (rssi > -70) return 'GOOD'
          if (rssi > -85 && rssi <= -70) return 'FAIR'
          if (rssi > -100 && rssi <= -85) return 'POOR'
          return 'BAD'
        }

        const calculateCounts = (value, layer) => {
          const key =
            value === null || value === undefined || value === '' ? 'UNKNOWN' : value
          legendCounts[layer] = {
            ...legendCounts[layer],
            [key]: !(key in legendCounts[layer]) ? 1 : legendCounts[layer][key] + 1,
          }
        }

        apiResponse?.forEach((unit) => {
          const { originalFloor, predictedFloor, originalName, predictedName } =
            parseUnitName(unit.name, unit.floor)

          const floor = originalFloor || predictedFloor || null
          const name = predictedName ?? originalName
          const humanizedSnooze = unit.snooze ? 'YES' : 'NO'

          floors.add(floor)
          buckets.add(name)

          calculateCounts(unit.status, LAYER_STATUS)
          calculateCounts(humanizedSnooze, LAYER_SNOOZE)
          if (isEmpty(unit.devices)) {
            const layers = [
              LAYER_AWS_STATUS,
              LAYER_PROVISION,
              LAYER_OPERATIONAL_STATE,
              LAYER_CONN_GUIDANCE_MODE,
              LAYER_RSSI,
              LAYER_AP,
              LAYER_SSID,
              LAYER_SSID_MIGRATION,
              LAYER_RESET_REASON,
              LAYER_ERROR,
              LAYER_FW,
              LAYER_MIC,
              LAYER_NOISE,
            ]
            layers.forEach((layer) => calculateCounts(null, layer))
          }

          mappedUnits[`${floor}:${name}`] = {
            ...unit,
            parsedName: name,
            humanizedSnooze,
            devices: unit.devices.map((device) => {
              const {
                awsStatus,
                provision,
                fw,
                ssid,
                ssidMigrationStatus,
                operationalState,
                connectivityGuidanceMode,
                ap,
                errorConditions: errConditions,
                resetReason,
                hasMicrophone,
                noiseEnabled,
              } = device

              const humanizedRssi = getHumanizedRssi(device.rssi)
              const haveErrors = isEmpty(errConditions) ? 'NO' : 'YES'

              // do not count multiple devices in the same unit
              if (unit.devices.length === 1) {
                Object.entries({
                  [LAYER_AWS_STATUS]: awsStatus,
                  [LAYER_PROVISION]: provision,
                  [LAYER_OPERATIONAL_STATE]: operationalState,
                  [LAYER_CONN_GUIDANCE_MODE]: connectivityGuidanceMode,
                  [LAYER_RSSI]: humanizedRssi,
                  [LAYER_AP]: ap,
                  [LAYER_SSID]: ssid,
                  [LAYER_SSID_MIGRATION]: ssidMigrationStatus,
                  [LAYER_ERROR]: isEmpty(errConditions) ? 'NO' : 'YES',
                  [LAYER_RESET_REASON]: resetReason,
                  [LAYER_FW]: fw,
                  [LAYER_MIC]: hasMicrophone,
                  [LAYER_NOISE]: noiseEnabled,
                }).forEach(([layer, value]) => calculateCounts(value, layer))
              }

              if (fw) uniqFW.add(fw)
              if (ssid) uniqSSID.add(ssid)
              if (ap) uniqAP.add(ap)

              return { ...device, humanizedRssi, haveErrors }
            }),
          }
        })

        buckets.forEach((bucket) => {
          tableRows.push(
            [...floors].reduce(
              (acc, floor) => ({
                ...acc,
                id: bucket,
                [floor]: mappedUnits[`${floor}:${bucket}`],
              }),
              {},
            ),
          )
        })

        const green = '#33A08E'
        const yellow = '#EBDB2F'
        const orange = '#E19147'
        const red = '#D16565'
        const grey = '#C4C4C4'

        const colorsPool = [
          '#7F66B3',
          '#669CC3',
          '#CF8473',
          '#344B78',
          '#547E7C',
          '#C55A86',
          '#F5B961',
          '#B8B8B8',
          ...generateColors({ count: 2000 }),
        ]

        const legend = {
          [LAYER_STATUS]: {
            ...unitStatuses?.reduce(
              (acc, status, i) => ({
                ...acc,
                [status]: {
                  color: colorsPool[i % colorsPool.length],
                  count: legendCounts[LAYER_STATUS][status] ?? 0,
                },
              }),
              {},
            ),
            ONLINE: { color: green, count: legendCounts[LAYER_STATUS].ONLINE ?? 0 },
            OFFLINE: {
              color: yellow,
              count: legendCounts[LAYER_STATUS].OFFLINE ?? 0,
            },
            INSTALLED: {
              color: red,
              count: legendCounts[LAYER_STATUS].INSTALLED ?? 0,
            },
          },
          [LAYER_SNOOZE]: {
            YES: { color: yellow, count: legendCounts[LAYER_SNOOZE].YES ?? 0 },
            NO: { color: green, count: legendCounts[LAYER_SNOOZE].NO ?? 0 },
          },
          [LAYER_AWS_STATUS]: {
            [DEVICE_AWS_ONLINE]: {
              color: green,
              count: legendCounts[LAYER_AWS_STATUS][DEVICE_AWS_ONLINE] ?? 0,
            },
            [DEVICE_AWS_OFFLINE]: {
              color: yellow,
              count: legendCounts[LAYER_AWS_STATUS][DEVICE_AWS_OFFLINE] ?? 0,
            },
            [DEVICE_AWS_UNKNOWN]: {
              color: grey,
              count: legendCounts[LAYER_AWS_STATUS][DEVICE_AWS_UNKNOWN] ?? 0,
            },
            [DEVICE_AWS_MQTTS_BLOCKED]: {
              color: orange,
              count: legendCounts[LAYER_AWS_STATUS][DEVICE_AWS_MQTTS_BLOCKED] ?? 0,
            },
            UNKNOWN: {
              color: grey,
              count: legendCounts[LAYER_AWS_STATUS].UNKNOWN ?? 0,
            },
          },
          [LAYER_PROVISION]: {
            0: {
              color: red,
              count: legendCounts[LAYER_PROVISION][0] ?? 0,
            },
            1: {
              color: yellow,
              count: legendCounts[LAYER_PROVISION][1] ?? 0,
            },
            2: {
              color: green,
              count: legendCounts[LAYER_PROVISION][2] ?? 0,
            },
            UNKNOWN: {
              color: grey,
              count: legendCounts[LAYER_PROVISION].UNKNOWN ?? 0,
            },
          },
          [LAYER_OPERATIONAL_STATE]: {
            ...operationalStates?.reduce(
              (acc, state, i) => ({
                ...acc,
                [state]: {
                  color: colorsPool[i % colorsPool.length],
                  count: legendCounts[LAYER_OPERATIONAL_STATE][state] ?? 0,
                },
              }),
              {},
            ),
            UNKNOWN: {
              color: grey,
              count: legendCounts[LAYER_OPERATIONAL_STATE].UNKNOWN ?? 0,
            },
          },
          [LAYER_CONN_GUIDANCE_MODE]: {
            ...connectivityGuidanceModes?.reduce(
              (acc, mode, i) => ({
                ...acc,
                [mode]: {
                  color: colorsPool[i % colorsPool.length],
                  count: legendCounts[LAYER_CONN_GUIDANCE_MODE][mode] ?? 0,
                },
              }),
              {},
            ),
            UNKNOWN: {
              color: grey,
              count: legendCounts[LAYER_CONN_GUIDANCE_MODE].UNKNOWN ?? 0,
            },
          },
          [LAYER_RSSI]: {
            GOOD: { color: green, count: legendCounts[LAYER_RSSI].GOOD ?? 0 },
            FAIR: { color: yellow, count: legendCounts[LAYER_RSSI].FAIR ?? 0 },
            POOR: { color: orange, count: legendCounts[LAYER_RSSI].POOR ?? 0 },
            BAD: { color: red, count: legendCounts[LAYER_RSSI].BAD ?? 0 },
            UNKNOWN: { color: grey, count: legendCounts[LAYER_RSSI].UNKNOWN ?? 0 },
          },
          [LAYER_AP]: {
            ...[...uniqAP].reduce(
              (acc, ap, i) => ({
                ...acc,
                [ap]: {
                  color: colorsPool[i % colorsPool.length],
                  count: legendCounts[LAYER_AP][ap] ?? 0,
                },
              }),
              {},
            ),
            UNKNOWN: { color: grey, count: legendCounts[LAYER_AP].UNKNOWN ?? 0 },
          },
          [LAYER_SSID]: {
            ...[...uniqSSID].reduce(
              (acc, ssid, i) => ({
                ...acc,
                [ssid]: {
                  color: colorsPool[i % colorsPool.length],
                  count: legendCounts[LAYER_SSID][ssid] ?? 0,
                },
              }),
              {},
            ),
            UNKNOWN: { color: grey, count: legendCounts[LAYER_SSID].UNKNOWN ?? 0 },
          },
          [LAYER_SSID_MIGRATION]: {
            NO_MIGRATION_IN_PROGRESS: {
              color: grey,
              count: legendCounts[LAYER_SSID_MIGRATION].NO_MIGRATION_IN_PROGRESS ?? 0,
            },
            NOT_READY: {
              color: red,
              count: legendCounts[LAYER_SSID_MIGRATION].NOT_READY ?? 0,
            },
            READY: {
              color: yellow,
              count: legendCounts[LAYER_SSID_MIGRATION].READY ?? 0,
            },
            COMPLETE: {
              color: green,
              count: legendCounts[LAYER_SSID_MIGRATION].COMPLETE ?? 0,
            },
            UNKNOWN: {
              color: grey,
              count: legendCounts[LAYER_SSID_MIGRATION].UNKNOWN ?? 0,
            },
          },
          [LAYER_RESET_REASON]: {
            ...resetReasons?.reduce(
              (acc, reason, i) => ({
                ...acc,
                [reason]: {
                  color: colorsPool[i % colorsPool.length],
                  count: legendCounts[LAYER_RESET_REASON][reason] ?? 0,
                },
              }),
              {},
            ),
            UNKNOWN: {
              color: grey,
              count: legendCounts[LAYER_RESET_REASON].UNKNOWN ?? 0,
            },
          },
          [LAYER_ERROR]: {
            YES: { color: red, count: legendCounts[LAYER_ERROR].YES ?? 0 },
            NO: { color: green, count: legendCounts[LAYER_ERROR].NO ?? 0 },
            UNKNOWN: { color: grey, count: legendCounts[LAYER_ERROR].UNKNOWN ?? 0 },
          },
          [LAYER_FW]: {
            ...[...uniqFW].reduce(
              (acc, fw, i) => ({
                ...acc,
                [fw]: {
                  color: colorsPool[i % colorsPool.length],
                  count: legendCounts[LAYER_FW][fw] ?? 0,
                },
              }),
              {},
            ),
            UNKNOWN: { color: grey, count: legendCounts[LAYER_FW].UNKNOWN ?? 0 },
          },
          [LAYER_MIC]: {
            YES: { color: yellow, count: legendCounts[LAYER_MIC].YES ?? 0 },
            NO: { color: green, count: legendCounts[LAYER_MIC].NO ?? 0 },
            UNKNOWN: { color: grey, count: legendCounts[LAYER_MIC].UNKNOWN ?? 0 },
          },
          [LAYER_NOISE]: {
            YES: { color: yellow, count: legendCounts[LAYER_NOISE].YES ?? 0 },
            NO: { color: green, count: legendCounts[LAYER_NOISE].NO ?? 0 },
            UNKNOWN: { color: grey, count: legendCounts[LAYER_NOISE].UNKNOWN ?? 0 },
          },
        }

        const sortLegendData = (data) => {
          const result = {}
          Object.keys(data).forEach((layer) => {
            const entries = Object.entries(data[layer])
            const processedEntries = entries.sort(([, a], [, b]) => b.count - a.count)
            result[layer] = Object.fromEntries(processedEntries)
          })
          return result
        }

        const data = {
          raw: apiResponse,
          table: {
            columns: [...floors].sort((a, b) => a - b),
            rows: tableRows.sort((a, b) => a.id - b.id),
            legend: sortLegendData(legend),
          },
        }

        await dispatch({
          type: CONNECTIVITY_MAP_DATA_LOADED,
          payload: data,
          meta: { status: 'succeeded' },
        })
        return data
      } catch (err) {
        if (!isAbortError(err)) {
          dispatch({
            type: CONNECTIVITY_MAP_DATA_FAILED,
            payload: err,
            meta: { status: 'failed' },
          })
          throw err
        }
        return null
      }
    },
}
